import { useState, useEffect } from "react";
import { navLinks, companyInfo } from "../data/mock";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (href) => {
    setIsMobileOpen(false);
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          isScrolled
            ? "backdrop-blur-xl border-b"
            : "border-b border-transparent"
        }`}
        style={{
          height: "80px",
          background: isScrolled ? "rgba(15, 18, 37, 0.92)" : "transparent",
          borderColor: isScrolled ? "rgba(123, 104, 238, 0.12)" : "transparent",
        }}
      >
        <div className="w-full h-full flex items-center justify-between" style={{ padding: "0 7.6923%" }}>
          {/* Logo */}
          <a
            href="#"
            className="flex items-center gap-3 no-underline"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <div
              className="w-10 h-10 flex items-center justify-center rounded-lg"
              style={{ background: "linear-gradient(135deg, #7B68EE, #5B4BD6)" }}
            >
              <span className="text-white font-bold text-lg" style={{ fontFamily: "'Kode Mono', monospace" }}>M</span>
            </div>
            <span
              className="font-semibold text-xl hidden sm:block"
              style={{ fontFamily: "'Kode Mono', monospace", letterSpacing: "-0.02em", color: "#F0F0FF" }}
            >
              {companyInfo.name}
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => scrollTo(link.href)}
                className="bg-transparent border-none cursor-pointer transition-colors duration-300"
                style={{
                  fontSize: "16px",
                  fontWeight: 400,
                  fontFamily: "'Kode Mono', monospace",
                  color: "#8585A8",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = "#F0F0FF"; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = "#8585A8"; }}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* CTA + Mobile Toggle */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => scrollTo("#contact")}
              className="hidden md:inline-flex items-center gap-3 border-none cursor-pointer transition-all duration-400"
              style={{
                background: "#7B68EE",
                color: "#FFFFFF",
                padding: "12px 24px",
                fontSize: "15px",
                fontWeight: 500,
                borderRadius: "8px",
                fontFamily: "'Kode Mono', monospace",
                minHeight: "46px",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#6A58DD"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#7B68EE"; }}
            >
              Get In Touch
              <ArrowUpRight size={16} />
            </button>

            <button
              className="md:hidden bg-transparent border-none cursor-pointer p-2"
              style={{ color: "#F0F0FF" }}
              onClick={() => setIsMobileOpen(!isMobileOpen)}
            >
              {isMobileOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col items-center justify-center gap-8"
            style={{ paddingTop: "80px", background: "#0F1225" }}
          >
            {navLinks.map((link, i) => (
              <motion.button
                key={link.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                onClick={() => scrollTo(link.href)}
                className="bg-transparent border-none cursor-pointer text-2xl"
                style={{ fontFamily: "'Kode Mono', monospace", fontWeight: 500, color: "#F0F0FF" }}
              >
                {link.label}
              </motion.button>
            ))}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: navLinks.length * 0.1 }}
              onClick={() => scrollTo("#contact")}
              className="inline-flex items-center gap-3 border-none cursor-pointer mt-4"
              style={{
                background: "#7B68EE",
                color: "#FFFFFF",
                padding: "16px 32px",
                fontSize: "18px",
                fontWeight: 500,
                borderRadius: "8px",
                fontFamily: "'Kode Mono', monospace",
              }}
            >
              Get In Touch
              <ArrowUpRight size={20} />
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
